import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-base';

export interface MyStorePagedResponse<T> {
  rows: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface MyStoreStore {
  id: number;
  tenant_id: number;
  code?: string | null;
  name: string;
  store_type: 'MATRIX' | 'BRANCH';
  matrix_store_id?: number | null;
  manager_user_id?: number | null;
  manager_user_name?: string | null;
  address_zip?: string | null;
  address_street?: string | null;
  address_number?: string | null;
  address_complement?: string | null;
  address_neighborhood?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_country?: string | null;
  active: number;
  created_at: string;
  updated_at?: string | null;
}

export interface MyStoreClient {
  id: number;
  tenant_id: number;
  code?: string | null;
  name: string;
  document_number?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile_phone?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  notes?: string | null;
  active: number;
  created_at: string;
  updated_at?: string | null;
}

export interface MyStoreCategory {
  id: number;
  tenant_id: number;
  name: string;
  description?: string | null;
  active: number;
  created_at: string;
  updated_at?: string | null;
}

export interface MyStoreItem {
  id: number;
  tenant_id: number;
  category_id?: number | null;
  category_name?: string | null;
  code?: string | null;
  name: string;
  item_type: 'PRODUCT' | 'SERVICE';
  sku?: string | null;
  unit_name?: string | null;
  cost_price: number;
  sale_price: number;
  stock_enabled: number;
  minimum_stock: number;
  active: number;
  created_at: string;
  updated_at?: string | null;
}

export interface MyStoreSupplier {
  id: number;
  tenant_id: number;
  name: string;
  document_number?: string | null;
  email?: string | null;
  phone?: string | null;
  contact_name?: string | null;
  notes?: string | null;
  active: number;
  created_at: string;
  updated_at?: string | null;
}

export interface MyStoreStoreUser {
  id: number;
  store_id: number;
  user_id: number;
  user_name?: string | null;
  email?: string | null;
  role_name: string;
  can_manage: number;
  can_sell: number;
  can_buy: number;
  can_stock: number;
  can_cash: number;
  active: number;
}

export interface MyStoreBudget {
  id: number;
  tenant_id: number;
  store_id: number;
  store_name?: string | null;
  client_id?: number | null;
  client_name?: string | null;
  seller_user_id?: number | null;
  seller_name?: string | null;
  code?: string | null;
  status: string;
  valid_until?: string | null;
  notes?: string | null;
  subtotal: number;
  discount_amount: number;
  total_amount: number;
  converted_sale_id?: number | null;
  created_at: string;
  updated_at?: string | null;
  items?: MyStoreLineItem[];
}

export interface MyStoreSale {
  id: number;
  tenant_id: number;
  store_id: number;
  store_name?: string | null;
  client_id?: number | null;
  client_name?: string | null;
  seller_user_id?: number | null;
  seller_name?: string | null;
  source_budget_id?: number | null;
  code?: string | null;
  status: string;
  sold_at?: string | null;
  notes?: string | null;
  subtotal: number;
  discount_amount: number;
  total_amount: number;
  paid_amount: number;
  created_at: string;
  updated_at?: string | null;
  items?: MyStoreLineItem[];
  payments?: MyStorePayment[];
}

export interface MyStorePurchase {
  id: number;
  tenant_id: number;
  store_id: number;
  store_name?: string | null;
  supplier_id?: number | null;
  supplier_name?: string | null;
  buyer_user_id?: number | null;
  buyer_name?: string | null;
  code?: string | null;
  status: string;
  purchased_at?: string | null;
  notes?: string | null;
  subtotal: number;
  discount_amount: number;
  total_amount: number;
  created_at: string;
  updated_at?: string | null;
  items?: MyStoreLineItem[];
}

export interface MyStoreCashSession {
  id: number;
  store_id: number;
  store_name?: string | null;
  opened_by: number;
  opened_by_name?: string | null;
  closed_by?: number | null;
  status: 'OPEN' | 'CLOSED';
  opened_at: string;
  closed_at?: string | null;
  opening_amount: number;
  closing_amount?: number | null;
  expected_amount: number;
  notes?: string | null;
}

export interface MyStoreLineItem {
  id?: number;
  item_id?: number | null;
  description: string;
  quantity: number;
  unit_price?: number;
  unit_cost?: number;
  discount_amount?: number;
  total_amount?: number;
}

export interface MyStorePayment {
  id?: number;
  payment_method: string;
  amount: number;
  paid_at?: string | null;
  notes?: string | null;
}

@Injectable({ providedIn: 'root' })
export class MystoreService {
  private readonly baseUrl = `${API_BASE_URL}/mystore`;

  constructor(private http: HttpClient) {}

  private withTenant(params?: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });
    return httpParams;
  }

  getLookups(tenantId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/lookups`, { params: this.withTenant({ tenant_id: tenantId }) });
  }

  getDashboard(tenantId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/dashboard`, { params: this.withTenant({ tenant_id: tenantId }) });
  }

  getStores(tenantId: number, params: Record<string, any>): Observable<MyStorePagedResponse<MyStoreStore>> {
    return this.http.get<MyStorePagedResponse<MyStoreStore>>(`${this.baseUrl}/stores`, { params: this.withTenant({ tenant_id: tenantId, ...params }) });
  }

  createStore(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/stores`, payload);
  }

  updateStore(storeId: number, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/stores/${storeId}`, payload);
  }

  deleteStore(tenantId: number, userId: number, storeId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/stores/${storeId}`, { params: this.withTenant({ tenant_id: tenantId, user_id: userId }) });
  }

  getStoreUsers(tenantId: number, storeId: number): Observable<MyStoreStoreUser[]> {
    return this.http.get<MyStoreStoreUser[]>(`${this.baseUrl}/stores/${storeId}/users`, { params: this.withTenant({ tenant_id: tenantId }) });
  }

  addStoreUser(storeId: number, payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/stores/${storeId}/users`, payload);
  }

  updateStoreUser(linkId: number, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/store-users/${linkId}`, payload);
  }

  deleteStoreUser(tenantId: number, userId: number, linkId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/store-users/${linkId}`, { params: this.withTenant({ tenant_id: tenantId, user_id: userId }) });
  }

  getClients(tenantId: number, params: Record<string, any>): Observable<MyStorePagedResponse<MyStoreClient>> {
    return this.http.get<MyStorePagedResponse<MyStoreClient>>(`${this.baseUrl}/clients`, { params: this.withTenant({ tenant_id: tenantId, ...params }) });
  }

  createClient(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/clients`, payload);
  }

  updateClient(clientId: number, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/clients/${clientId}`, payload);
  }

  deleteClient(tenantId: number, userId: number, clientId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/clients/${clientId}`, { params: this.withTenant({ tenant_id: tenantId, user_id: userId }) });
  }

  getCategories(tenantId: number, params: Record<string, any>): Observable<MyStorePagedResponse<MyStoreCategory>> {
    return this.http.get<MyStorePagedResponse<MyStoreCategory>>(`${this.baseUrl}/categories`, { params: this.withTenant({ tenant_id: tenantId, ...params }) });
  }

  createCategory(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/categories`, payload);
  }

  updateCategory(categoryId: number, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/categories/${categoryId}`, payload);
  }

  deleteCategory(tenantId: number, userId: number, categoryId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/categories/${categoryId}`, { params: this.withTenant({ tenant_id: tenantId, user_id: userId }) });
  }

  getItems(tenantId: number, params: Record<string, any>): Observable<MyStorePagedResponse<MyStoreItem>> {
    return this.http.get<MyStorePagedResponse<MyStoreItem>>(`${this.baseUrl}/items`, { params: this.withTenant({ tenant_id: tenantId, ...params }) });
  }

  createItem(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/items`, payload);
  }

  updateItem(itemId: number, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/items/${itemId}`, payload);
  }

  deleteItem(tenantId: number, userId: number, itemId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/items/${itemId}`, { params: this.withTenant({ tenant_id: tenantId, user_id: userId }) });
  }

  getSuppliers(tenantId: number, params: Record<string, any>): Observable<MyStorePagedResponse<MyStoreSupplier>> {
    return this.http.get<MyStorePagedResponse<MyStoreSupplier>>(`${this.baseUrl}/suppliers`, { params: this.withTenant({ tenant_id: tenantId, ...params }) });
  }

  createSupplier(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/suppliers`, payload);
  }

  updateSupplier(supplierId: number, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/suppliers/${supplierId}`, payload);
  }

  deleteSupplier(tenantId: number, userId: number, supplierId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/suppliers/${supplierId}`, { params: this.withTenant({ tenant_id: tenantId, user_id: userId }) });
  }

  getStockPositions(tenantId: number, params: Record<string, any> = {}): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock/positions`, { params: this.withTenant({ tenant_id: tenantId, ...params }) });
  }

  getStockMovements(tenantId: number, params: Record<string, any> = {}): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/stock/movements`, { params: this.withTenant({ tenant_id: tenantId, ...params }) });
  }

  createStockMovement(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/stock/movements`, payload);
  }

  getBudgets(tenantId: number, params: Record<string, any>): Observable<MyStorePagedResponse<MyStoreBudget>> {
    return this.http.get<MyStorePagedResponse<MyStoreBudget>>(`${this.baseUrl}/budgets`, { params: this.withTenant({ tenant_id: tenantId, ...params }) });
  }

  getBudget(tenantId: number, budgetId: number): Observable<MyStoreBudget> {
    return this.http.get<MyStoreBudget>(`${this.baseUrl}/budgets/${budgetId}`, { params: this.withTenant({ tenant_id: tenantId }) });
  }

  createBudget(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/budgets`, payload);
  }

  updateBudget(budgetId: number, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/budgets/${budgetId}`, payload);
  }

  convertBudgetToSale(budgetId: number, payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/budgets/${budgetId}/convert-to-sale`, payload);
  }

  deleteBudget(tenantId: number, userId: number, budgetId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/budgets/${budgetId}`, { params: this.withTenant({ tenant_id: tenantId, user_id: userId }) });
  }

  getSales(tenantId: number, params: Record<string, any>): Observable<MyStorePagedResponse<MyStoreSale>> {
    return this.http.get<MyStorePagedResponse<MyStoreSale>>(`${this.baseUrl}/sales`, { params: this.withTenant({ tenant_id: tenantId, ...params }) });
  }

  getSale(tenantId: number, saleId: number): Observable<MyStoreSale> {
    return this.http.get<MyStoreSale>(`${this.baseUrl}/sales/${saleId}`, { params: this.withTenant({ tenant_id: tenantId }) });
  }

  createSale(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/sales`, payload);
  }

  updateSale(saleId: number, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/sales/${saleId}`, payload);
  }

  addSalePayment(saleId: number, payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/sales/${saleId}/payments`, payload);
  }

  deleteSale(tenantId: number, userId: number, saleId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/sales/${saleId}`, { params: this.withTenant({ tenant_id: tenantId, user_id: userId }) });
  }

  getPurchases(tenantId: number, params: Record<string, any>): Observable<MyStorePagedResponse<MyStorePurchase>> {
    return this.http.get<MyStorePagedResponse<MyStorePurchase>>(`${this.baseUrl}/purchases`, { params: this.withTenant({ tenant_id: tenantId, ...params }) });
  }

  getPurchase(tenantId: number, purchaseId: number): Observable<MyStorePurchase> {
    return this.http.get<MyStorePurchase>(`${this.baseUrl}/purchases/${purchaseId}`, { params: this.withTenant({ tenant_id: tenantId }) });
  }

  createPurchase(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/purchases`, payload);
  }

  updatePurchase(purchaseId: number, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/purchases/${purchaseId}`, payload);
  }

  deletePurchase(tenantId: number, userId: number, purchaseId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/purchases/${purchaseId}`, { params: this.withTenant({ tenant_id: tenantId, user_id: userId }) });
  }

  getCashSessions(tenantId: number): Observable<MyStoreCashSession[]> {
    return this.http.get<MyStoreCashSession[]>(`${this.baseUrl}/cash/sessions`, { params: this.withTenant({ tenant_id: tenantId }) });
  }

  openCashSession(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/cash/sessions/open`, payload);
  }

  closeCashSession(sessionId: number, payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/cash/sessions/${sessionId}/close`, payload);
  }

  getCashMovements(tenantId: number, sessionId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/cash/sessions/${sessionId}/movements`, { params: this.withTenant({ tenant_id: tenantId }) });
  }

  createCashMovement(sessionId: number, payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/cash/sessions/${sessionId}/movements`, payload);
  }

  getRevenueReport(tenantId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/reports/faturamento`, { params: this.withTenant({ tenant_id: tenantId }) });
  }

  getStockReport(tenantId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/reports/estoque`, { params: this.withTenant({ tenant_id: tenantId }) });
  }

  getSalesReport(tenantId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/reports/vendas`, { params: this.withTenant({ tenant_id: tenantId }) });
  }
}
